import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ZoomIn, Check, Loader2 } from "lucide-react";

// Produce a cropped JPEG dataURL from the source image and pixel crop area.
async function getCroppedImg(imageSrc, cropPixels, outWidth = 1200) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const cw = Math.round(cropPixels.width);
  const ch = Math.round(cropPixels.height);
  if (cw < 2 || ch < 2) return imageSrc; // degenerate crop area: keep original image
  const scale = outWidth / cw;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cw * scale));
  canvas.height = Math.max(1, Math.round(ch * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    cropPixels.x, cropPixels.y, cw, ch,
    0, 0, canvas.width, canvas.height
  );
  const url = canvas.toDataURL("image/jpeg", 0.85);
  return url && url.length > 20 ? url : imageSrc;
}

// Crop + zoom + reposition a cover image to a fixed banner aspect before saving.
export default function CoverCropDialog({ open, image, aspect = 3, outWidth = 1200, cropShape = "rect", title = "Ritaglia la copertina", subtitle = "Trascina per posizionare e usa lo zoom: la copertina avrà sempre proporzioni ordinate.", onOpenChange, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const confirm = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const url = await getCroppedImg(image, pixels, outWidth);
      onCropped(url);
      onOpenChange(false);
    } finally {
      setBusy(false);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="cover-crop-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">{title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">{subtitle}</p>
        <div className="relative w-full h-56 bg-slate-900 rounded-xl overflow-hidden mt-2">
          {image && (
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={cropShape}
              showGrid={cropShape !== "round"}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="cover"
            />
          )}
        </div>
        <div className="flex items-center gap-3 mt-4">
          <ZoomIn className="h-4 w-4 text-slate-400 shrink-0" />
          <input
            type="range" min={1} max={3} step={0.01} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            data-testid="cover-crop-zoom"
            className="w-full accent-[#E63946]"
          />
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="cover-crop-cancel" className="flex-1 h-11 rounded-xl font-bold">Annulla</Button>
          <Button onClick={confirm} disabled={busy || !pixels} data-testid="cover-crop-confirm" className="flex-1 h-11 rounded-xl font-bold gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Applica
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
